import AccessTimeOutlined from '@mui/icons-material/AccessTimeOutlined';
import AccountBalanceOutlined from '@mui/icons-material/AccountBalanceOutlined';
import AttachMoneyOutlined from '@mui/icons-material/AttachMoneyOutlined';
import BalanceOutlined from '@mui/icons-material/BalanceOutlined';
import BlockOutlined from '@mui/icons-material/BlockOutlined';
import CalculateOutlined from '@mui/icons-material/CalculateOutlined';
import DescriptionOutlined from '@mui/icons-material/DescriptionOutlined';
import FamilyRestroomOutlined from '@mui/icons-material/FamilyRestroom';
import GavelOutlined from '@mui/icons-material/GavelOutlined';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import MoneyOffOutlined from '@mui/icons-material/MoneyOff';
import PeopleOutlined from '@mui/icons-material/PeopleOutline';
import PolicyOutlined from '@mui/icons-material/Policy';
import RestoreOutlined from '@mui/icons-material/Restore';
import SecurityOutlined from '@mui/icons-material/Security';
import ShieldOutlined from '@mui/icons-material/Shield';
import VerifiedUserOutlined from '@mui/icons-material/VerifiedUser';
import VideocamOutlined from '@mui/icons-material/Videocam';
import VisibilityOutlined from '@mui/icons-material/Visibility';
import { Box, Button, Container, Grid, Typography } from '@mui/material';
import React from 'react';
import { Link } from 'react-router-dom';
import type { SvgIconProps } from '@mui/material/SvgIcon';
import type { WhyItMatters } from '../types/whyItMatters';

/**
 * MUI icon lookup map.
 * Maps icon identifier strings from billIndex.json to actual MUI icon components.
 * Falls back to InfoOutlined for unknown identifiers.
 *
 * @see specs/013-why-bill-matters/data-model.md — Icon Map Entity
 */
const ICON_MAP: Record<string, React.ComponentType<SvgIconProps>> = {
  GavelOutlined,
  RestoreOutlined,
  VerifiedUserOutlined,
  BlockOutlined,
  ShieldOutlined,
  AccountBalanceOutlined,
  BalanceOutlined,
  VisibilityOutlined,
  VideocamOutlined,
  AttachMoneyOutlined,
  FamilyRestroomOutlined,
  AccessTimeOutlined,
  DescriptionOutlined,
  PolicyOutlined,
  SecurityOutlined,
  PeopleOutlined,
  MoneyOffOutlined,
  CalculateOutlined,
};

/** Resolve an icon identifier to a MUI component, with InfoOutlined fallback. */
const resolveIcon = (iconName: string): React.ComponentType<SvgIconProps> => ICON_MAP[iconName] ?? InfoOutlined;

interface WhyBillMattersProps {
  whyItMatters?: WhyItMatters;
  /** Country slug — Take Action CTAs only render for 'united-states' */
  country?: string;
}

/**
 * "Why This Bill Matters" section for bill detail pages.
 *
 * Renders a heading, summary, 3 benefit cards in a responsive grid,
 * an optional emotional callout, and an optional legislative precedent banner.
 *
 * Returns null when whyItMatters data is absent.
 *
 * @see specs/013-why-bill-matters/spec.md
 */
const WhyBillMatters: React.FC<WhyBillMattersProps> = ({ whyItMatters, country = 'united-states' }) => {
  if (!whyItMatters) return null;

  const { summary, benefits, emotionalCallout, legislativePrecedent } = whyItMatters;
  const isUS = country === 'united-states';

  return (
    <Box
      component="section"
      aria-labelledby="why-bill-matters-heading"
      sx={{
        py: { xs: 4, sm: 6, md: 8 },
        backgroundColor: 'background.default',
      }}
    >
      <Container maxWidth="lg">
        {/* Section Heading */}
        <Typography
          id="why-bill-matters-heading"
          variant="h2"
          component="h2"
          sx={{
            textAlign: 'center',
            mb: 2,
            background: 'linear-gradient(135deg, #FFD700 0%, #B8860B 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            fontWeight: 700,
          }}
        >
          Why This Bill Matters
        </Typography>

        {/* Summary Text */}
        <Typography
          variant="body1"
          sx={{
            textAlign: 'center',
            maxWidth: 800,
            mx: 'auto',
            mb: { xs: 4, md: 6 },
            color: 'text.secondary',
            fontSize: { xs: '1rem', md: '1.125rem' },
            lineHeight: 1.8,
          }}
        >
          {summary}
        </Typography>

        {/* Benefit Cards Grid — xs=12, sm=6, md=4 */}
        <Grid container spacing={{ xs: 2, sm: 3, md: 4 }}>
          {benefits.map((benefit, index) => {
            const IconComponent = resolveIcon(benefit.icon);
            return (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Box
                  sx={{
                    p: { xs: 3, md: 4 },
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    backgroundColor: 'background.paper',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    textAlign: 'center',
                    transition: 'border-color 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                    '&:hover': {
                      borderColor: '#FFD700',
                      boxShadow: '0 4px 20px rgba(255, 215, 0, 0.15)',
                    },
                  }}
                >
                  {/* Icon — decorative, hidden from assistive tech */}
                  <Box
                    sx={{
                      mb: 2,
                      width: 56,
                      height: 56,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: 'linear-gradient(135deg, rgba(255, 215, 0, 0.15) 0%, rgba(184, 134, 11, 0.15) 100%)',
                    }}
                  >
                    <IconComponent aria-hidden="true" sx={{ fontSize: 28, color: '#FFD700' }} />
                  </Box>

                  {/* Card Title */}
                  <Typography
                    variant="h3"
                    component="h3"
                    sx={{
                      mb: 1.5,
                      fontWeight: 600,
                      fontSize: { xs: '1.1rem', md: '1.25rem' },
                      color: 'text.primary',
                    }}
                  >
                    {benefit.title}
                  </Typography>

                  {/* Card Description */}
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'text.secondary',
                      lineHeight: 1.7,
                      fontSize: { xs: '0.875rem', md: '0.95rem' },
                    }}
                  >
                    {benefit.description}
                  </Typography>
                </Box>
              </Grid>
            );
          })}
        </Grid>

        {/* Emotional Callout — conditional render (T006 / US3) */}
        {emotionalCallout && (
          <Box
            sx={{
              mt: { xs: 4, md: 6 },
              p: { xs: 3, sm: 4, md: 5 },
              borderRadius: 2,
              background: 'linear-gradient(135deg, rgba(255, 215, 0, 0.08) 0%, rgba(184, 134, 11, 0.08) 100%)',
              borderLeft: '4px solid #FFD700',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h3"
              component="h3"
              sx={{
                mb: 2,
                fontWeight: 600,
                fontSize: { xs: '1.25rem', md: '1.5rem' },
                color: '#FFD700',
              }}
            >
              {emotionalCallout.title}
            </Typography>
            <Typography
              variant="body1"
              sx={{
                maxWidth: 720,
                mx: 'auto',
                mb: 2,
                color: 'text.secondary',
                lineHeight: 1.8,
                fontSize: { xs: '0.95rem', md: '1.05rem' },
              }}
            >
              {emotionalCallout.description}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 700,
                fontStyle: 'italic',
                color: 'text.primary',
                fontSize: { xs: '0.95rem', md: '1.05rem' },
              }}
            >
              {emotionalCallout.tagline}
            </Typography>
          </Box>
        )}

        {/* Legislative Precedent Banner — conditional render (T007 / US4) */}
        {isUS && legislativePrecedent && (
          <Box
            sx={{
              mt: { xs: 4, md: 6 },
              p: { xs: 3, sm: 4 },
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              backgroundColor: 'background.paper',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="overline"
              sx={{
                color: '#FFD700',
                letterSpacing: 2,
                fontWeight: 700,
                mb: 1,
                display: 'block',
              }}
            >
              {legislativePrecedent.label}
            </Typography>
            <Typography
              variant="h6"
              component="p"
              sx={{
                fontWeight: 700,
                mb: 1.5,
                color: 'text.primary',
              }}
            >
              {legislativePrecedent.citation}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                maxWidth: 640,
                mx: 'auto',
                mb: 3,
                color: 'text.secondary',
                lineHeight: 1.7,
              }}
            >
              {legislativePrecedent.description}
            </Typography>
            <Button
              component={Link}
              to={legislativePrecedent.ctaRoute || '/legislator-lookup'}
              variant="outlined"
              size="large"
              sx={{
                px: 4,
                py: 1.5,
                fontWeight: 700,
                textTransform: 'none',
                fontSize: '1rem',
              }}
            >
              {legislativePrecedent.ctaText}
            </Button>
          </Box>
        )}

        {/* Default Contact CTA — renders only for US, when no legislative precedent banner is present */}
        {isUS && !legislativePrecedent && (
          <Box
            sx={{
              mt: { xs: 4, md: 6 },
              p: { xs: 3, sm: 4 },
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              backgroundColor: 'background.paper',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="overline"
              sx={{
                color: '#FFD700',
                letterSpacing: 2,
                fontWeight: 700,
                mb: 1,
                display: 'block',
              }}
            >
              TAKE ACTION
            </Typography>
            <Typography
              variant="body2"
              sx={{
                maxWidth: 640,
                mx: 'auto',
                mb: 3,
                color: 'text.secondary',
                lineHeight: 1.7,
              }}
            >
              This bill needs advocates in every state. Contact your legislator today and ask them to sponsor this
              legislation in your state.
            </Typography>
            <Button
              component={Link}
              to="/legislator-lookup"
              variant="outlined"
              size="large"
              sx={{
                px: 4,
                py: 1.5,
                fontWeight: 700,
                textTransform: 'none',
                fontSize: '1rem',
              }}
            >
              Contact Your Legislator
            </Button>
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default WhyBillMatters;
